import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { refreshCheckIntoRoom } from './services'

const CHECK_IN_REFRESH_LOG_EVENT_ID = 'log-check-in-refresh'

function readStoredCheckInDetails() {
  try {
    const stored = localStorage.getItem('checkedInDetails')
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

function resolveStableCheckInCreatedTime(checkInData, chamberRoom = null, existingCheckIn = null) {
  return (
    chamberRoom?.mentorLastCheckedIn ||
    checkInData?.checkInCreatedTime ||
    existingCheckIn?.checkInCreatedTime ||
    (existingCheckIn?.logEventId !== CHECK_IN_REFRESH_LOG_EVENT_ID
      ? existingCheckIn?.createdTime
      : null) ||
    (checkInData?.logEventId !== CHECK_IN_REFRESH_LOG_EVENT_ID ? checkInData?.createdTime : null) ||
    existingCheckIn?.createdTime ||
    checkInData?.createdTime ||
    null
  )
}

export function normalizeCheckInDetailsForStorage(
  checkInData,
  chamberRoom = null,
  existingCheckIn = readStoredCheckInDetails(),
) {
  if (!checkInData) {
    return checkInData
  }

  const stableCheckInCreatedTime = resolveStableCheckInCreatedTime(
    checkInData,
    chamberRoom,
    existingCheckIn,
  )
  const normalized = {
    ...checkInData,
  }

  if (stableCheckInCreatedTime) {
    normalized.createdTime = stableCheckInCreatedTime
    normalized.checkInCreatedTime = stableCheckInCreatedTime
  }

  if (checkInData?.logEventId === CHECK_IN_REFRESH_LOG_EVENT_ID && checkInData?.createdTime) {
    normalized.lastCheckInRefreshCreatedTime = checkInData.createdTime
  }

  if (chamberRoom?.mentorLastCheckinRefreshed) {
    normalized.lastCheckInRefreshCreatedTime = chamberRoom.mentorLastCheckinRefreshed
  }

  return normalized
}

/**
 * BroadcastChannel for cross-tab communication
 * Allows multiple tabs to sync check-in state
 */
let checkInChannel = null

/**
 * Initialize BroadcastChannel for check-in sync
 * @returns {BroadcastChannel|null} The channel instance or null if not supported
 */
export function initCheckInBroadcastChannel() {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
    console.warn('[CheckInSync] BroadcastChannel not supported in this browser')
    return null
  }

  if (!checkInChannel) {
    checkInChannel = new window.BroadcastChannel('check-in-sync')

    // Listen for messages from other tabs
    checkInChannel.onmessage = (event) => {
      const { type, data } = event.data


      switch (type) {
        case 'check-in-updated':
          if (data) {
            const normalizedData = normalizeCheckInDetailsForStorage(data, data.chamberRoom)
            localStorage.setItem('checkedInDetails', JSON.stringify(normalizedData))
            // Also update peekedInRoomDetails if provided
            if (data.chamberRoom) {
              localStorage.setItem('peekedInRoomDetails', JSON.stringify(data.chamberRoom))
            }
            // Dispatch event for components to react
            window.dispatchEvent(
              new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedIn, {
                detail: {
                  roomData: data.chamberRoom,
                  usageLog: data,
                },
              }),
            )
          }
          break

        case 'check-in-removed':
          localStorage.removeItem('checkedInDetails')
          localStorage.removeItem('peekedInRoomDetails')
          localStorage.removeItem('roomDataEventTypes')
          // Dispatch event for components to react
          window.dispatchEvent(new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, { detail: {} }))
          break

        case 'check-in-validated':
          // Another tab validated, update our timestamp
          if (data?.lastValidated) {
            const current = localStorage.getItem('checkedInDetails')
            if (current) {
              try {
                const parsed = JSON.parse(current)
                parsed.lastValidated = data.lastValidated
                localStorage.setItem('checkedInDetails', JSON.stringify(parsed))
              } catch (e) {
                console.error('[CheckInSync] Error updating validation timestamp:', e)
              }
            }
          }
          break

        default:
          console.warn('[CheckInSync] Unknown message type:', type)
      }
    }
  }

  return checkInChannel
}

/**
 * Broadcast check-in update to other tabs
 * @param {Object} checkInData - The check-in data to broadcast
 * @param {Object} chamberRoom - Optional chamber room data
 */
export function broadcastCheckInUpdate(checkInData, chamberRoom = null) {
  if (!checkInChannel) {
    initCheckInBroadcastChannel()
  }

  if (checkInChannel) {
    checkInChannel.postMessage({
      type: 'check-in-updated',
      data: {
        ...checkInData,
        chamberRoom,
        lastValidated: new Date().toISOString(),
      },
    })
  }
}

/**
 * Broadcast check-in removal to other tabs
 */
export function broadcastCheckInRemoval() {
  if (!checkInChannel) {
    initCheckInBroadcastChannel()
  }

  if (checkInChannel) {
    checkInChannel.postMessage({
      type: 'check-in-removed',
    })
  }
}

/**
 * Broadcast validation timestamp to other tabs
 */
export function broadcastValidationTimestamp() {
  if (!checkInChannel) {
    initCheckInBroadcastChannel()
  }

  if (checkInChannel) {
    checkInChannel.postMessage({
      type: 'check-in-validated',
      data: {
        lastValidated: new Date().toISOString(),
      },
    })
  }
}

/**
 * Maximum age for check-in data before it's considered stale (5 minutes)
 */
const MAX_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Check if check-in data is stale
 * @param {Object} checkedInDetails - The check-in details object
 * @returns {boolean} True if data is stale
 */
export function isCheckInStale(checkedInDetails) {
  if (!checkedInDetails) return true

  const lastValidated = checkedInDetails?.lastValidated
  if (!lastValidated) return true

  try {
    const lastValidatedDate = new Date(lastValidated)
    const now = new Date()
    const age = now - lastValidatedDate

    return age > MAX_STALE_TIME_MS
  } catch (e) {
    console.error('[CheckInSync] Error checking stale data:', e)
    return true
  }
}

/**
 * Retry configuration for resilient server calls
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504], // Timeout, rate limit, server errors
  retryableErrors: ['ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND', 'ECONNRESET'],
}

export const CHECK_IN_REFRESH_STATUS = {
  active: 'active',
  inactive: 'inactive',
  unknown: 'unknown',
}

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Check if error is retryable
 * @param {Error|Object} error - The error to check
 * @returns {boolean} True if error is retryable
 */
function isRetryableError(error) {
  // Check for network errors
  if (error?.code && RETRY_CONFIG.retryableErrors.includes(error.code)) {
    return true
  }

  // Check for axios timeout/abort errors
  if (error?.message) {
    const message = error.message.toLowerCase()
    if (message.includes('timeout') || message.includes('network error') || message.includes('aborted')) {
      return true
    }
  }

  // Check for response status codes
  if (error?.response?.status) {
    return RETRY_CONFIG.retryableStatusCodes.includes(error.response.status)
  }

  // Check for axios error types
  if (error?.isAxiosError) {
    // Network errors are retryable
    if (!error.response) {
      return true
    }
    // Server errors are retryable
    if (error.response.status >= 500) {
      return true
    }
  }

  return false
}

/**
 * Calculate delay for retry attempt with exponential backoff
 * @param {number} attemptNumber - Current attempt number (0-indexed)
 * @returns {number} Delay in milliseconds
 */
function calculateRetryDelay(attemptNumber) {
  const delay = RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attemptNumber)
  return Math.min(delay, RETRY_CONFIG.maxDelay)
}

/**
 * Fetch current check-in status from server with retry logic
 * @param {number} attemptNumber - Current attempt number (for retries)
 * @returns {Promise<Object|null>} Check-in data or null if not checked in
 */
export async function fetchCurrentCheckInStatus(attemptNumber = 0) {
  const result = await refreshCurrentCheckInStatus(attemptNumber)
  if (result?.status === CHECK_IN_REFRESH_STATUS.active) {
    return {
      emrUsageLog: result.emrUsageLog,
      chamberRoom: result.chamberRoom,
    }
  }
  return null
}

export async function refreshCurrentCheckInStatus(attemptNumber = 0) {
  try {
    // Try to get check-in status from server
    const localCheckIn = localStorage.getItem('checkedInDetails')

    if (!localCheckIn) {
      return { status: CHECK_IN_REFRESH_STATUS.inactive, reason: 'no-local-check-in' }
    }

    const parsedLocal = JSON.parse(localCheckIn)


    // Use refresh endpoint to validate current status
    const response = await refreshCheckIntoRoom({
      selectedCheckInEventOption: parsedLocal?.selectedCheckInEventOption,
      clinicBrandId: parsedLocal?.clinicBrandId,
      clinicLocationId: parsedLocal?.clinicLocationId,
      checkinMethod: parsedLocal?.checkinMethod,
      chamberRoomId: parsedLocal?.chamberRoomId,
      apiParam: 'emr-usage-log',
    })

    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const emrUsageLog = response?.data?.data?.['emr-usage-log']
      const chamberRoom = response?.data?.data?.['chamber-room']

      if (emrUsageLog) {
        const normalizedEmrUsageLog = normalizeCheckInDetailsForStorage(
          {
            ...emrUsageLog,
            lastValidated: new Date().toISOString(),
          },
          chamberRoom,
          parsedLocal,
        )
        return {
          status: CHECK_IN_REFRESH_STATUS.active,
          emrUsageLog: normalizedEmrUsageLog,
          chamberRoom,
        }
      }
    }

    if (isRetryableError(response) && attemptNumber < RETRY_CONFIG.maxRetries) {
      const delay = calculateRetryDelay(attemptNumber)
      await sleep(delay)
      return refreshCurrentCheckInStatus(attemptNumber + 1)
    }

    if (isRetryableError(response)) {
      return {
        status: CHECK_IN_REFRESH_STATUS.unknown,
        reason: 'retryable-refresh-failed',
        error: response,
      }
    }

    // If refresh fails with non-retryable error, check-in is no longer valid
    return {
      status: CHECK_IN_REFRESH_STATUS.inactive,
      reason: 'server-rejected-check-in',
      response,
    }
  } catch (error) {
    console.error(`[CheckInSync] Error fetching current check-in status (attempt ${attemptNumber + 1}):`, error)

    // Check if error is retryable and we haven't exceeded max retries
    if (isRetryableError(error) && attemptNumber < RETRY_CONFIG.maxRetries) {
      const delay = calculateRetryDelay(attemptNumber)

      await sleep(delay)

      // Retry with incremented attempt number
      return refreshCurrentCheckInStatus(attemptNumber + 1)
    }

    // Non-retryable error or max retries exceeded
    if (attemptNumber >= RETRY_CONFIG.maxRetries) {
      console.error('[CheckInSync] Max retries exceeded, giving up')
    } else {
      console.error('[CheckInSync] Non-retryable error, not retrying')
    }

    return {
      status: CHECK_IN_REFRESH_STATUS.unknown,
      reason: 'refresh-threw',
      error,
    }
  }
}

/**
 * Validate and sync check-in status with server
 * This should be called on page load to ensure localStorage is in sync
 * Uses resilient retry logic for server calls
 * @returns {Promise<Object>} Result object with sync status
 */
export async function validateAndSyncCheckIn() {

  const localCheckInStr = localStorage.getItem('checkedInDetails')
  const localCheckIn = localCheckInStr ? JSON.parse(localCheckInStr) : null

  // If no local check-in, nothing to validate
  if (!localCheckIn) {
    return {
      synced: true,
      action: 'none',
      reason: 'no-local-check-in',
    }
  }

  // Check if data is stale
  const isStale = isCheckInStale(localCheckIn)


  // Track validation attempts
  let refreshResult = null

  // Try to fetch from server with retry logic (handled inside fetchCurrentCheckInStatus)
  try {
    refreshResult = await refreshCurrentCheckInStatus()
  } catch (error) {
    console.error('[CheckInSync] Error during server fetch (after retries):', error)
    refreshResult = {
      status: CHECK_IN_REFRESH_STATUS.unknown,
      reason: 'refresh-threw',
      error,
    }
  }

  // Handle server response
  if (refreshResult?.status === CHECK_IN_REFRESH_STATUS.active) {
    // Server has check-in - update localStorage with server data
    const updatedCheckIn = normalizeCheckInDetailsForStorage(
      {
        ...refreshResult.emrUsageLog,
        lastValidated: new Date().toISOString(),
        validationError: false, // Clear any previous validation errors
      },
      refreshResult.chamberRoom,
      localCheckIn,
    )

    localStorage.setItem('checkedInDetails', JSON.stringify(updatedCheckIn))

    if (refreshResult.chamberRoom) {
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(refreshResult.chamberRoom))
    }

    // Broadcast update to other tabs
    broadcastCheckInUpdate(updatedCheckIn, refreshResult.chamberRoom)

    // Check if data actually changed
    const dataChanged = JSON.stringify(localCheckIn) !== JSON.stringify(updatedCheckIn)


    return {
      synced: true,
      action: dataChanged ? 'updated' : 'validated',
      reason: isStale ? 'stale-data-refreshed' : 'validation-success',
      dataChanged,
    }
  } else if (refreshResult?.status === CHECK_IN_REFRESH_STATUS.unknown) {
    console.warn('[CheckInSync] Unable to validate check-in, keeping local data')

    const updatedCheckIn = {
      ...localCheckIn,
      lastValidated: new Date().toISOString(),
      validationError: true,
      validationErrorReason: refreshResult?.reason || 'unknown-refresh-error',
    }
    localStorage.setItem('checkedInDetails', JSON.stringify(updatedCheckIn))

    return {
      synced: false,
      action: 'kept-local',
      reason: refreshResult?.reason || 'unknown-refresh-error',
      error: refreshResult?.error?.message || refreshResult?.error?.response?.status,
      keptLocalData: true,
    }
  } else if (refreshResult?.status === CHECK_IN_REFRESH_STATUS.inactive) {
    // Server fetch failed or returned null
    // This could mean:
    // 1. Server error (network issues, etc.) - keep local data
    // 2. Server says no check-in - remove local data
    console.warn('[CheckInSync] Server has no check-in, but local does - removing local check-in')

    localStorage.removeItem('checkedInDetails')
    localStorage.removeItem('peekedInRoomDetails')
    localStorage.removeItem('roomDataEventTypes')

    // Broadcast removal to other tabs
    broadcastCheckInRemoval()

    // Dispatch event for components
    window.dispatchEvent(new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, { detail: {} }))

    return {
      synced: true,
      action: 'removed',
      reason: refreshResult?.reason || 'server-no-check-in',
    }
  }

  // Fallback (shouldn't reach here)
  return {
    synced: false,
    action: 'none',
    reason: 'unknown-error',
    error: 'Unexpected validation state',
  }
}

/**
 * Update check-in data with validation timestamp
 * @param {Object} checkInData - The check-in data to update
 */
export function updateCheckInWithValidation(checkInData, chamberRoom = null) {
  const updated = normalizeCheckInDetailsForStorage(
    {
      ...checkInData,
      lastValidated: new Date().toISOString(),
    },
    chamberRoom,
  )
  localStorage.setItem('checkedInDetails', JSON.stringify(updated))
  broadcastCheckInUpdate(updated, chamberRoom)
  broadcastValidationTimestamp()
}

/**
 * Cleanup BroadcastChannel
 */
export function cleanupCheckInBroadcastChannel() {
  if (checkInChannel) {
    checkInChannel.close()
    checkInChannel = null
  }
}
